import * as React from 'react'
import styles from './ComposerArticlePreview.pcss'
import { addComposerImages, clearComposerContent, setComposerLinkImage, setPostAsPhoto } from 'services/compose'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { composerPostAsPhotoSelector } from 'services/compose/selectors'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { mdiImageMultipleOutline, mdiClose } from '@mdi/js'
import Icon from '@mdi/react'

interface ComposerArticlePreviewProps {
  article: {
    title: string
    url: string
    socialUrl?: string
    imageUrl: string
    description?: string
  }
  articleImages?: string[]
  selectedImage?: string
  className?: string
  onClear: (linkUrl: string) => void
}

export function ComposerArticlePreview(props: ComposerArticlePreviewProps) {
  const { articleImages, article, selectedImage, onClear } = props
  const images = articleImages || [props.article.imageUrl]
  const intl = useIntl()
  const dispatch = useDispatch()
  const postAsPhoto = useSelector(composerPostAsPhotoSelector)
  const showCarousel = postAsPhoto && images.length > 1
  const imageStyle = postAsPhoto ? undefined : { backgroundImage: `url(${selectedImage || props.article.imageUrl})` }

  const onTogglePostAsPhoto = () => {
    dispatch(setPostAsPhoto(!postAsPhoto))
  }

  const clearLink = () => {
    onClear(props.article.url)
  }

  const addAllImages = () => {
    dispatch(clearComposerContent())
    dispatch(addComposerImages(images))
  }

  const onSelectedImageChange = (imgUrl: string) => {
    dispatch(setComposerLinkImage({ linkUrl: article.url, imageUrl: imgUrl }))
  }

  return (
    <div className={`${styles.article} ${props.className || ''}`} data-testid="composer-article-preview">
      <div className={`${styles['img-box']} ${postAsPhoto ? styles.carousel : ''}`} style={imageStyle}>
        {postAsPhoto && (
          <img src={selectedImage || images[0]} />
        )}
        {postAsPhoto && (
          <div className={styles['img-actions']}>
            <Tooltip title={intl.formatMessage({ id: 'actions.clear' })} placement="right">
              <IconButton size="small" onClick={clearLink}>
                <Icon path={mdiClose} size="16px" />
              </IconButton>
            </Tooltip>
            {images.length > 1 && (
              <Tooltip title="Carousel post" placement="right">
                <IconButton size="small" onClick={addAllImages}>
                  <Icon path={mdiImageMultipleOutline} size="16px" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      <div className={`${styles.details} ${postAsPhoto ? styles.hidden : ''}`}>
        <a
          href={props.article.url}
          rel="noopener"
          target="_blank"
          className={`${styles.link} text-ellipsis`}
        >
          {props.article.url}
        </a>
        <h3 className={styles.title} title={props.article.title}>
          <a
            href={props.article.socialUrl || props.article.url}
            rel="noopener"
            target="_blank"
          >
            {props.article.title}
          </a>
        </h3>
        {props.article.description && (
          <p className={styles.description}>{props.article.description}</p>
        )}
      </div>
      <div className={styles['img-box-footer']}>
        <FormControlLabel
          label={<FormattedMessage id="composer.labels.articles.post-as-photo" />}
          control={(
            <Checkbox
              size="small"
              checked={postAsPhoto}
              disableRipple
              color="primary"
              classes={{ checked: styles.checked, root: styles['cb-root'] }}
              onChange={onTogglePostAsPhoto}
            />
          )}
          className={styles.cb}
          classes={{ label: styles['cb-label'] }}
        />
        {showCarousel && (
          <IndexSelector
            images={images}
            selectedImage={selectedImage}
            onChange={onSelectedImageChange}
          />
        )}
      </div>
    </div>
  )
}

function IndexSelector(props: { images: string[], selectedImage?: string, onChange: (url: string) => void }) {
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const imageUrl = event.currentTarget.getAttribute('data-image-url')
    if (imageUrl) {
      props.onChange(imageUrl)
    }
  }

  return (
    <div className={styles['index-picker']}>
      {props.images.map((url) => (
        <div
          key={url}
          data-image-url={url}
          className={`${styles.index} ${url === props.selectedImage ? styles.active : ''}`}
          onClick={handleClick}
        />
      ))}
    </div>
  )
}
